import type { DailyCall } from '@daily-co/daily-js'
import { DailyProvider } from '@daily-co/daily-react'
import type { PendingStoreKiosk } from '@gozoki/api-types'
import { AuthContext, type AuthContextType } from '../utils/context/auth.context'
import {
  ClientEntryContext,
  type ClientEntryContextType,
} from '../utils/context/clientEntry.context'

import { AbilityProvider } from '@casl/react'
import { type Right, hasOneRight, hasRights } from '@gozoki/tools'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createAppAbility } from '../utils/ability'

import {
  attachSocketToken,
  getCurrentAdminRbacPermissionsOptions,
  setAiSocketUrl,
  setGozokiApiWebSocketManager,
  setStreamSocketUrl,
  useAdminMe,
} from '@gozoki/api'
import { ThemeProvider } from '@mui/material'
import * as Sentry from '@sentry/react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { setToken } from '../utils/api'
import { VisioContext, type VisioContextType } from '../utils/context/visio.context'
import { materialTheme } from '../utils/theme/material.theme'

interface GlobalProviderProps {
  children: React.ReactNode
}

const GlobalProvider = ({ children }: GlobalProviderProps) => {
  const me = useAdminMe()
  const rbacPermissions = useQuery({
    ...getCurrentAdminRbacPermissionsOptions(),
    enabled: !!me.data,
    retry: false,
  })
  const navigate = useNavigate()

  const [shouldStartCall, setShouldStartCall] = useState(false)
  const [kioskId, setKioskId] = useState<number | null>(null)
  const [storeId, setStoreId] = useState<number | null>(null)
  const [includeVideo, setIncludeVideo] = useState(false)
  const [openingEventId, setOpeningEventId] = useState<number | null>(null)
  const [callLabel, setCallLabel] = useState<string | null>(null)
  const [isCallInProgress, setIsCallInProgress] = useState(false)
  const [callObject, setCallObject] = useState<null | DailyCall>(null)
  const callObjectRef = useRef<null | DailyCall>(null)
  const disposedCallObjects = useRef(new WeakSet<DailyCall>())
  const callReservedRef = useRef(false)

  const [pendingKiosks, setPendingKiosks] = useState<PendingStoreKiosk[]>([])

  const askCall = useCallback(
    (
      newKioskId: number,
      newStoreId: number,
      newIncludeVideo: boolean,
      newOpeningEventId?: number,
      newCallLabel?: string,
    ) => {
      if (callReservedRef.current || isCallInProgress || shouldStartCall) {
        return
      }

      callReservedRef.current = true
      setKioskId(newKioskId)
      setStoreId(newStoreId)
      setIncludeVideo(newIncludeVideo)
      setOpeningEventId(newOpeningEventId ?? null)
      setCallLabel(newCallLabel ?? null)
      setShouldStartCall(true)
    },
    [isCallInProgress, shouldStartCall],
  )

  const startCall = useCallback(() => {
    setShouldStartCall(false)
    setIsCallInProgress(true)
  }, [])

  const updateCallObject = useCallback((nextCallObject: DailyCall | null) => {
    callObjectRef.current = nextCallObject
    setCallObject(nextCallObject)
  }, [])

  const clearCallObject = useCallback(() => {
    const activeCallObject = callObjectRef.current
    callObjectRef.current = null
    setCallObject(null)
    if (!activeCallObject || disposedCallObjects.current.has(activeCallObject)) return

    disposedCallObjects.current.add(activeCallObject)
    void (async () => {
      try {
        await activeCallObject.leave()
      } catch {
        // Daily may already have left the room; destruction still has to run.
      }
      try {
        await activeCallObject.destroy()
      } catch {
        // Destruction is idempotent from the application's point of view.
      }
    })()
  }, [])

  const endCall = useCallback(() => {
    clearCallObject()
    setShouldStartCall(false)
    setKioskId(null)
    setStoreId(null)
    setOpeningEventId(null)
    setCallLabel(null)
    setIsCallInProgress(false)
    callReservedRef.current = false
  }, [clearCallObject])

  const askEntry = useCallback((newPendingKiosk: PendingStoreKiosk) => {
    setPendingKiosks((prevKiosks) => [...prevKiosks, newPendingKiosk])
  }, [])

  const openClientForPendingKiosk = useCallback(
    (processedKioskId: number, openPage: boolean) => {
      setPendingKiosks((prevKiosks) => {
        const kioskToProcess = prevKiosks.find((kiosk) => kiosk.kioskId === processedKioskId)

        if (kioskToProcess && openPage) {
          navigate({
            search: { storeId: kioskToProcess.storeId },
            to: '/cameras/new',
          })
        }
        return prevKiosks.filter((kiosk) => kiosk.kioskId !== processedKioskId)
      })
    },
    [navigate],
  )

  useEffect(() => {
    if (me?.data?.socketToken) {
      attachSocketToken(me.data.socketToken)
    }
    if (me?.data?.socketAiUrl) {
      setAiSocketUrl(me.data.socketAiUrl)
    }
    if (me?.data?.socketStreamUrl) {
      setStreamSocketUrl(me.data.socketStreamUrl)
    }
    if (me?.data?.socketUrl) {
      setGozokiApiWebSocketManager(me.data.socketUrl)
    }
  }, [me])

  const logout = useCallback(async () => {
    await setToken(null)
    await me.refetch()
    Sentry.setUser(null)
    navigate({ to: '/login' }) // Navigate back to login page
  }, [me, navigate])

  const currentAbility = useMemo(
    () =>
      createAppAbility(rbacPermissions.data?.grants ?? [], rbacPermissions.data?.grantsV2 ?? [], {
        enforcementEnabled: rbacPermissions.data?.enforcementEnabled ?? true,
        permissionV2Enabled: rbacPermissions.data?.permissionV2Enabled ?? false,
      }),
    [rbacPermissions.data],
  )

  const authContextValue: AuthContextType = useMemo(
    () => ({
      authenticated:
        !!me.data &&
        (hasOneRight(me.data.adminRights, [
          'READER',
          'STOCK_READER',
          'ACCOUNTING_READER',
          'STORE_OPERATOR',
          'SUPPORT_OPERATOR',
          'ADMIN',
          'ACCOUNTING_ADMIN',
          'STOCK_ADMIN',
          'QUALITY',
          // Un compte client du suivi projet n'a que ce bit et un rôle **v2** : ses permissions
          // atterrissent dans `grantsV2`, jamais dans `grants` — le OU ci-dessous ne le sauve pas.
          'PROJECT_TRACKING',
        ]) ||
          (rbacPermissions.data?.grants.length ?? 0) > 0),
      user: me.data ?? null,
      refetchUser: me.refetch,
      isLoading: me.isLoading || (!!me.data && rbacPermissions.isLoading),
      logout,
      hasRights: (rights: Right[]) => !!me.data && hasRights(me.data.adminRights, rights),
      hasOneRight: (rights: Right[]) => !!me.data && hasOneRight(me.data.adminRights, rights),
      rbacLoading: !!me.data && rbacPermissions.isLoading,
    }),
    [me.data, me.refetch, me.isLoading, logout, rbacPermissions.data, rbacPermissions.isLoading],
  )

  const visioContextValue: VisioContextType = useMemo(
    () => ({
      shouldStartCall,
      kioskId,
      storeId,
      includeVideo,
      openingEventId,
      isCallInProgress,
      callObject,
      callLabel,
      setCallObject: updateCallObject,
      clearCallObject,
      askCall,
      startCall,
      endCall,
    }),
    [
      kioskId,
      shouldStartCall,
      storeId,
      includeVideo,
      openingEventId,
      isCallInProgress,
      callObject,
      callLabel,
      updateCallObject,
      clearCallObject,
      endCall,
      startCall,
      askCall,
    ],
  )

  const clientEntryContextValue: ClientEntryContextType = useMemo(
    () => ({
      pendingKiosks,
      askEntry,
      openClientForPendingKiosk,
    }),
    [pendingKiosks, askEntry, openClientForPendingKiosk],
  )

  if (me.isLoading || (me.data && rbacPermissions.isPending)) {
    return null
  }

  return (
    <AbilityProvider value={currentAbility}>
      <AuthContext.Provider value={authContextValue}>
        <VisioContext.Provider value={visioContextValue}>
          <DailyProvider callObject={callObject}>
            <ClientEntryContext.Provider value={clientEntryContextValue}>
              <ThemeProvider theme={materialTheme}>{children}</ThemeProvider>
            </ClientEntryContext.Provider>
          </DailyProvider>
        </VisioContext.Provider>
      </AuthContext.Provider>
    </AbilityProvider>
  )
}

export default GlobalProvider
